
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { type OfferWall, offerWalls as fallbackOfferWalls } from '@/lib/data';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy, writeBatch, where } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface OfferWallContextType {
  offerWalls: OfferWall[];
  addOfferWall: (wall: Omit<OfferWall, 'id'>) => Promise<void>;
  updateOfferWall: (id: string, updatedWall: Partial<Omit<OfferWall, 'id'>>) => Promise<void>;
  deleteOfferWall: (id: string) => Promise<void>;
  loading: boolean;
  refreshOfferWalls: () => void;
}

const OfferWallContext = createContext<OfferWallContextType | undefined>(undefined);

export const OfferWallProvider = ({ children }: { children: ReactNode }) => {
  const [offerWalls, setOfferWalls] = useState<OfferWall[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOfferWalls = useCallback(async () => {
    setLoading(true);
    try {
      const offerWallsCollection = collection(db, 'offerwalls');
      const q = query(offerWallsCollection, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      const wallsFromDb: OfferWall[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        } as OfferWall));
      
      setOfferWalls(wallsFromDb);

    } catch (error) {
      console.error("Failed to fetch offer walls from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load offer walls from the database. Please ensure Firestore is enabled." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOfferWalls();
  }, [fetchOfferWalls]);

  const addOfferWall = async (wallData: Omit<OfferWall, 'id'>) => {
    try {
      const offerWallsCollection = collection(db, 'offerwalls');
      const docRef = await addDoc(offerWallsCollection, wallData);
      setOfferWalls(prev => [...prev, {...wallData, id: docRef.id}].sort((a,b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error adding offer wall:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the offer wall." });
      throw error; // Re-throw to be caught by the calling component
    }
  };

  const updateOfferWall = async (id: string, updatedWallData: Partial<Omit<OfferWall, 'id'>>) => {
     try {
       if (Object.keys(updatedWallData).length === 0) {
           toast({ title: "No changes to save." });
           return;
       }

       const wallDocRef = doc(db, 'offerwalls', id);
       await updateDoc(wallDocRef, updatedWallData);
       
       setOfferWalls(prev =>
         prev.map(wall => (wall.id === id ? { ...wall, ...updatedWallData } : wall)).sort((a,b) => a.name.localeCompare(b.name))
       );
     } catch (error) {
       console.error("Error updating offer wall:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not update the offer wall." });
       throw error;
     }
  };

  const deleteOfferWall = async (id: string) => {
    try {
      const wallDocRef = doc(db, 'offerwalls', id);
      await deleteDoc(wallDocRef);
      setOfferWalls(prev => prev.filter(wall => wall.id !== id));
    } catch (error) {
       console.error("Error deleting offer wall:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the offer wall." });
       throw error;
    }
  };

  return (
    <OfferWallContext.Provider value={{ offerWalls, addOfferWall, updateOfferWall, deleteOfferWall, loading, refreshOfferWalls: fetchOfferWalls }}>
      {children}
    </OfferWallContext.Provider>
  );
};

export const useOfferWalls = () => {
  const context = useContext(OfferWallContext);
  if (context === undefined) {
    throw new Error('useOfferWalls must be used within an OfferWallProvider');
  }
  return context;
};
